/* eslint-disable react/no-array-index-key */
import React, { FC, useEffect, useState } from 'react'
import { Box, Flex, Image, SimpleCarousel } from '@moonpig/launchpad-components'
import { styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { spacing, theme } from '@moonpig/launchpad-theme'
import { FavouriteButton } from '../FavouriteButton'
import { useLocaleText } from './ImageCarouselVariant.locale'
import { ProductImage } from '../../types'
import { MoreLikeThisButton } from '../MoreLikeThisButton/MoreLikeThisButton'

const StyledImage = styled(Image)<{ p?: number }>`
  object-fit: contain;
  ${({ p }) => s({ p })}
`
const StyledCarousel = styled(SimpleCarousel)`
  padding: 0;
  & > div > ul {
    gap: 16px;
  }
  ${s({
    display: { xs: 'none', md: 'block' },
  })}
`

const StyledImageContainer = styled(Box)<{ showMoreLikeThis?: boolean }>`
  ${s({
    borderRadius: 4,
    overflow: 'hidden',
    bgcolor: 'colorBackground02',
    width: 1,
    borderColor: 'transparent',
    borderBottom: 3,
    borderTop: 3,
    borderLeft: 3,
    borderRight: 3,
  })}
  
    ${({ showMoreLikeThis }) =>
      showMoreLikeThis &&
      s({
        pb: 13,
        alignItems: 'center',
      })}

  &:is(button)[aria-selected='true'] {
    ${s({
      borderColor: 'colorInteractionSelectedState',
    })}
  }

  &:is(button):hover {
    ${s({
      borderColor: 'colorInteractionButtonHover',
    })}
  }
`

const StyledFavButton = styled(FavouriteButton)`
  ${s({
    position: 'absolute',
    top: 8,
    right: 8,
  })}
`

const ImageItem: FC<{
  title: string
  url: string
  zoomUrl?: string
  p?: number
  selected?: boolean
  onClick?: () => void
  showMoreLikeThis?: boolean
}> = ({
  title,
  url,
  zoomUrl,
  p,
  selected,
  onClick,
  showMoreLikeThis,
  ...props
}) => {
  return (
    <StyledImageContainer
      onClick={onClick}
      as={onClick ? 'button' : 'div'}
      aria-selected={selected}
      showMoreLikeThis={showMoreLikeThis}
      {...props}
    >
      <StyledImage
        aspectRatio={1}
        src={url}
        zoomSrc={zoomUrl}
        title={title}
        p={p}
        loading={selected ? 'eager' : 'lazy'}
      />
    </StyledImageContainer>
  )
}

const mapProductImages = (
  productImages: ProductImage[],
): Record<string, string>[] => {
  return productImages.map(image => ({
    small: image.small.webpUrl || image.small.jpgUrl,
    medium: image.medium.webpUrl || image.medium.jpgUrl,
    large: image.large.webpUrl || image.large.jpgUrl,
  }))
}

type Props = {
  productId: string
  title: string
  productImages: ProductImage[]
  initialSelectedIndex: number
  isFavourited: boolean
  onFavouriteToggle: () => void
  onImageSelected?: (event: { index: number }) => void
  showMoreLikeThis: boolean
}

export const ImageCarouselVariant: FC<Props> = ({
  productId,
  title,
  productImages,
  initialSelectedIndex,
  isFavourited,
  onFavouriteToggle,
  onImageSelected,
  showMoreLikeThis,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const t = useLocaleText()

  const carouselImages = mapProductImages(productImages)

  useEffect(() => {
    setSelectedIndex(initialSelectedIndex)
  }, [initialSelectedIndex, productId])

  return (
    <Flex
      mb={6}
      gap={spacing(6)({ theme })}
      flexDirection={'column'}
      maxWidth="30.25rem"
      margin="0 auto"
    >
      <Box
        position={'relative'}
        data-product-id={productId}
        className="product-image-carousel"
      >
        <ImageItem
          title={title}
          url={carouselImages[selectedIndex].large}
          zoomUrl={carouselImages[selectedIndex].large}
          p={8}
          showMoreLikeThis={showMoreLikeThis}
        />
        <StyledFavButton
          title={title}
          isFavourited={isFavourited}
          onFavouriteToggle={onFavouriteToggle}
          isCard
        />
        {showMoreLikeThis && <MoreLikeThisButton />}
      </Box>
      <StyledCarousel aria-label={t('image_carousel_aria_label')}>
        {carouselImages.map((image, index) => (
          <Box key={index} width={{ xs: '5rem', md: '6rem', lg: '7.35rem' }}>
            <ImageItem
              title={title}
              url={image.small}
              p={4}
              selected={selectedIndex === index}
              onClick={() => {
                setSelectedIndex(index)
                onImageSelected?.({ index })
              }}
            />
          </Box>
        ))}
      </StyledCarousel>
    </Flex>
  )
}
